// Brand Controls redesign — Overview tab.
//
// Five sections, all data-driven from VoiceProfile + GeneratedAttributes +
// BeforeAfterExample. The annotated specimen highlights only phrases that
// actually appear in the on-voice sentence, and shows a ledger note only
// when the backend supplied one (analysis.whyBetter) — nothing authored.

import React from "react";
import { Sparkles } from "lucide-react";
import type {
  VoiceProfile,
  GeneratedAttributes,
  BeforeAfterExample,
} from "../../../services/brand/brand-voice-service";
import { Eyebrow, Orb, ExtractedAttrCards } from "./shared";

const SPAN_COLORS = ["var(--alpha-teal)", "var(--signal-blue)", "var(--spectral-violet)"];
const SPAN_SUP = ["var(--teal-text)", "var(--signal-blue)", "var(--spectral-violet)"];

interface Annotation {
  num: number;
  color: string;
  supColor: string;
  phrase: string;
  title: string;
  note: string;
}

/**
 * Derive the annotated specimen: the on-voice sentence + up to 3 highlighted
 * phrases. Phrases are commonPhrases that literally appear in the sentence
 * (case-insensitive, first occurrence, non-overlapping). Titles come from
 * detected tone traits; notes only from analysis.whyBetter (real) — never
 * invented. Returns null when there is no on-voice sentence to show.
 */
function buildSpecimen(
  sentence: string | undefined,
  attrs: GeneratedAttributes | null,
  analysis?: BeforeAfterExample["analysis"]
): { sentence: string; annotations: Annotation[] } | null {
  if (!sentence || !sentence.trim()) return null;

  const phrases = (attrs?.commonPhrases || []).filter(Boolean);
  const traits = attrs?.tone?.detected || [];
  const why = analysis?.whyBetter || [];

  const lower = sentence.toLowerCase();
  const picked: { phrase: string; index: number }[] = [];
  const used: [number, number][] = [];

  for (const p of phrases) {
    if (picked.length >= 3) break;
    const at = lower.indexOf(p.toLowerCase());
    if (at < 0) continue;
    const end = at + p.length;
    const overlaps = used.some(([s, e]) => at < e && end > s);
    if (overlaps) continue;
    used.push([at, end]);
    picked.push({ phrase: sentence.slice(at, end), index: at });
  }

  picked.sort((a, b) => a.index - b.index);

  const annotations: Annotation[] = picked.map((p, i) => ({
    num: i + 1,
    color: SPAN_COLORS[i] || SPAN_COLORS[0],
    supColor: SPAN_SUP[i] || SPAN_SUP[0],
    phrase: p.phrase,
    title: traits[i] || "Signature phrase",
    note: why[i] || "",
  }));

  return { sentence, annotations };
}

/** Render the sentence with the picked phrases underlined + superscripted. */
function AnnotatedSentence({
  sentence,
  annotations,
}: {
  sentence: string;
  annotations: Annotation[];
}) {
  if (annotations.length === 0) {
    return (
      <p style={sentenceStyle}>{sentence}</p>
    );
  }

  // Build segments around the annotated phrases, in order of appearance.
  const lower = sentence.toLowerCase();
  const spans = annotations
    .map((a) => {
      const at = lower.indexOf(a.phrase.toLowerCase());
      return at < 0 ? null : { a, at, end: at + a.phrase.length };
    })
    .filter(Boolean) as { a: Annotation; at: number; end: number }[];
  spans.sort((x, y) => x.at - y.at);

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  spans.forEach((s, i) => {
    if (s.at > cursor) nodes.push(sentence.slice(cursor, s.at));
    nodes.push(
      <span key={`p${i}`} style={{ borderBottom: `2px solid ${s.a.color}`, paddingBottom: 1 }}>
        {sentence.slice(s.at, s.end)}
      </span>
    );
    nodes.push(
      <sup key={`s${i}`} style={{ color: s.a.supColor, fontSize: ".5em", fontWeight: 700, padding: "0 3px" }}>
        {s.a.num}
      </sup>
    );
    cursor = s.end;
  });
  if (cursor < sentence.length) nodes.push(sentence.slice(cursor));

  return <p style={sentenceStyle}>{nodes}</p>;
}

const sentenceStyle: React.CSSProperties = {
  fontSize: "clamp(22px,3.4vw,32px)",
  fontWeight: 600,
  letterSpacing: "-.02em",
  lineHeight: 1.5,
  color: "var(--foreground)",
  margin: 0,
  textWrap: "pretty",
};

export interface OverviewViewProps {
  brand: string;
  profile: VoiceProfile;
  attrs: GeneratedAttributes | null;
  beforeAfter: BeforeAfterExample | null;
  rwInput: string;
  onRwInput: (v: string) => void;
  runRewrite: () => void;
  rwPhase: "idle" | "working" | "done";
  rwResult: string;
  goSetup: () => void;
  goAdvanced: () => void;
}

export function OverviewView({
  brand,
  profile,
  attrs,
  beforeAfter,
  rwInput,
  onRwInput,
  runRewrite,
  rwPhase,
  rwResult,
  goSetup,
  goAdvanced,
}: OverviewViewProps) {
  const ba = beforeAfter || profile.beforeAfterExample || null;
  const specimen = buildSpecimen(ba?.afterText, attrs, ba?.analysis || undefined);
  const voiceTags = (attrs?.tone?.detected || []).slice(0, 6);
  const avoid = (profile.avoidList || []).slice(0, 3);
  const usageCount = profile.usageCount ?? 0;

  // avoid terms present in the rewrite input (honest swap chips)
  const rwStruck = (profile.avoidList || []).filter(
    (t) => t && rwInput.toLowerCase().includes(String(t).toLowerCase())
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
      {/* 01 — the specimen */}
      {specimen && (
        <section>
          <div style={{ marginBottom: 18 }}>
            <Eyebrow>01 · Your brand voice</Eyebrow>
          </div>
          <div
            style={{
              border: "1px solid var(--border)",
              borderRadius: 20,
              background: "var(--card)",
              boxShadow: "var(--elevation-sm)",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "clamp(26px,4vw,44px) clamp(24px,4vw,48px)" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--spectral-violet)",
                  marginBottom: 22,
                }}
              >
                <Orb size={22} icon={12} />
                This is how {brand} sounds, and why
              </div>

              <AnnotatedSentence sentence={specimen.sentence} annotations={specimen.annotations} />

              {specimen.annotations.length > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
                    gap: 16,
                    marginTop: 32,
                    paddingTop: 24,
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  {specimen.annotations.map((n) => (
                    <div key={n.num} style={{ display: "flex", gap: 11 }}>
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 9999,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#fff",
                          background: n.color,
                        }}
                      >
                        {n.num}
                      </span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)", marginBottom: 2 }}>
                          {n.title}
                        </div>
                        {n.note && (
                          <div style={{ fontSize: 12.5, color: "var(--muted-foreground)", lineHeight: 1.45 }}>
                            {n.note}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {avoid.length > 0 && (
              <div
                style={{
                  borderTop: "1px solid var(--border)",
                  background: "var(--muted)",
                  padding: "16px clamp(24px,4vw,48px)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    color: "var(--muted-foreground)",
                  }}
                >
                  It steers around
                </span>
                {avoid.map((t, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: 14,
                      color: "var(--muted-foreground)",
                      textDecoration: "line-through",
                      textDecorationColor: "var(--infrared-coral)",
                      textDecorationThickness: "2px",
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          {voiceTags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
              {voiceTags.map((t, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--foreground)",
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: 9999,
                    padding: "6px 14px",
                    boxShadow: "var(--elevation-sm)",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* 02 — live rewrite */}
      <section>
        <div style={{ marginBottom: 6 }}>
          <Eyebrow>02 · Watch it apply your voice</Eyebrow>
        </div>
        <p style={{ fontSize: 14, color: "var(--muted-foreground)", margin: "0 0 18px" }}>
          Paste any copy. I'll rewrite it in {brand}'s voice, steering around the terms you avoid.
        </p>
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 16,
            background: "var(--card)",
            boxShadow: "var(--elevation-sm)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "20px 22px" }}>
            <textarea
              className="bc-input"
              value={rwInput}
              onChange={(e) => onRwInput(e.target.value)}
              rows={3}
              placeholder="Paste a sentence or paragraph to hear it in your voice..."
              style={{
                width: "100%",
                padding: "12px 14px",
                border: "1px solid var(--border)",
                borderRadius: 11,
                background: "var(--background)",
                fontSize: 15,
                lineHeight: 1.5,
                fontFamily: "var(--font-sans)",
                color: "var(--foreground)",
                outline: "none",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button
                onClick={runRewrite}
                disabled={rwPhase === "working" || !rwInput.trim()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  background: "var(--orb-gradient)",
                  border: "none",
                  borderRadius: 9,
                  padding: "9px 16px",
                  cursor: rwPhase === "working" || !rwInput.trim() ? "default" : "pointer",
                  opacity: !rwInput.trim() ? 0.6 : 1,
                  boxShadow: "var(--shadow-spectral-violet-glow)",
                }}
              >
                {rwPhase === "working" ? (
                  <span
                    className="bc-spin"
                    style={{
                      width: 14,
                      height: 14,
                      border: "2px solid rgba(255,255,255,.4)",
                      borderTopColor: "#fff",
                      borderRadius: 9999,
                      display: "inline-block",
                    }}
                  />
                ) : (
                  <Sparkles style={{ width: 14, height: 14 }} strokeWidth={1.8} />
                )}
                {rwPhase === "working" ? "Rewriting..." : rwPhase === "done" ? "Rewrite again" : "Rewrite in our voice"}
              </button>
            </div>
          </div>
          {rwPhase === "done" && rwResult && (
            <div
              className="bc-fade"
              style={{
                borderTop: "1px solid var(--border)",
                background: "linear-gradient(160deg,var(--alpha-teal-5),transparent 70%),var(--muted)",
                padding: "22px 24px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: ".1em",
                    textTransform: "uppercase",
                    color: "var(--teal-text)",
                  }}
                >
                  Rewritten in your voice
                </span>
                <Sparkles style={{ width: 13, height: 13, color: "var(--teal-text)" }} strokeWidth={1.8} />
              </div>
              <p
                style={{
                  fontSize: "clamp(17px,2.2vw,21px)",
                  fontWeight: 600,
                  lineHeight: 1.5,
                  letterSpacing: "-.01em",
                  color: "var(--foreground)",
                  margin: rwStruck.length ? "0 0 14px" : 0,
                  textWrap: "pretty",
                }}
              >
                {rwResult}
              </p>
              {rwStruck.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {rwStruck.map((t, i) => (
                    <span
                      key={i}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        color: "var(--muted-foreground)",
                        background: "var(--card)",
                        border: "1px solid var(--border)",
                        borderRadius: 9999,
                        padding: "4px 11px",
                      }}
                    >
                      Steered around
                      <span
                        style={{
                          textDecoration: "line-through",
                          textDecorationColor: "var(--infrared-coral)",
                          textDecorationThickness: "2px",
                        }}
                      >
                        {t}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* 03 — before / after */}
      {ba && (ba.beforeText || ba.afterText) && (
        <section>
          <div style={{ marginBottom: 6 }}>
            <Eyebrow>03 · The difference it makes</Eyebrow>
          </div>
          <p style={{ fontSize: 14, color: "var(--muted-foreground)", margin: "0 0 18px" }}>
            Same brief, two voices. Left is what a generic model writes. Right is what AI writes once it knows you.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16 }}>
            <div style={{ border: "1px solid var(--border)", borderRadius: 16, padding: "clamp(22px,3vw,32px)", background: "var(--muted)" }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--muted-foreground)", marginBottom: 16 }}>
                Generic AI
              </div>
              <p style={{ fontSize: "clamp(17px,2.1vw,21px)", fontWeight: 400, lineHeight: 1.55, color: "var(--muted-foreground)", margin: 0, fontStyle: "italic" }}>
                {ba.beforeText}
              </p>
            </div>
            <div
              style={{
                border: "1px solid var(--alpha-teal-30)",
                borderRadius: 16,
                padding: "clamp(22px,3vw,32px)",
                background: "linear-gradient(160deg,var(--alpha-teal-5),transparent 70%),var(--card)",
                boxShadow: "var(--elevation-md)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--teal-text)" }}>
                  In your voice
                </span>
                <Sparkles style={{ width: 13, height: 13, color: "var(--teal-text)" }} strokeWidth={1.8} />
              </div>
              <p style={{ fontSize: "clamp(18px,2.3vw,23px)", fontWeight: 600, lineHeight: 1.5, letterSpacing: "-.01em", color: "var(--foreground)", margin: 0, textWrap: "pretty" }}>
                {ba.afterText}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* 04 — what defines it (4 extracted attribute cards) */}
      {attrs && (
        <section>
          <div style={{ marginBottom: 18 }}>
            <Eyebrow>04 · What defines it</Eyebrow>
          </div>
          <ExtractedAttrCards attrs={attrs} />
        </section>
      )}

      {/* 05 — reach strip */}
      <section
        style={{
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: "24px 26px",
          background: "var(--muted)",
          display: "flex",
          alignItems: "center",
          gap: 20,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ marginBottom: 8 }}>
            <Eyebrow>Where it's working</Eyebrow>
          </div>
          <p style={{ fontSize: 15, color: "var(--foreground)", lineHeight: 1.55, margin: 0 }}>
            {usageCount > 0 ? (
              <>
                This profile governs every asset Upmode generates. It shaped{" "}
                <strong style={{ fontWeight: 600 }}>{usageCount} assets</strong> across Action Items and Test &amp; Refine, and it's the
                baseline every simulation writes against.
              </>
            ) : (
              <>
                This profile governs every asset Upmode generates. No assets have been generated with it yet;
                new work from Action Items and Test &amp; Refine will be written in this voice.
              </>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={goSetup}
            style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 9, padding: "9px 15px", cursor: "pointer" }}
          >
            Re-extract voice
          </button>
          <button
            onClick={goAdvanced}
            style={{ fontSize: 13, fontWeight: 600, color: "var(--on-teal-text)", background: "var(--alpha-teal)", border: "none", borderRadius: 9, padding: "9px 15px", cursor: "pointer" }}
          >
            Refine attributes
          </button>
        </div>
      </section>
    </div>
  );
}
